import { useEffect, useState } from 'react'
import {Store, Search, Filter, Loader2, MapPin} from 'lucide-react'
import CouponCard from '../components/CouponCard'
import Newsletter from '../components/Newsletter'
import { lumi } from '../lib/lumi'

interface Coupon {
  _id: string
  title: string
  description: string
  code?: string
  discount: string
  expiryDate?: string
  verified?: boolean
  affiliateLink: string
  brand: string
  category?: string
  active?: boolean
  clicks?: number
}

const categories = [
  { id: 'all', name: 'All Categories', icon: Store },
  { id: 'spa-wellness', name: 'Spa & Wellness', icon: MapPin },
  { id: 'food-beverage', name: 'Food & Beverage', icon: Store },
  { id: 'entertainment', name: 'Entertainment', icon: Store },
  { id: 'travel-outdoor', name: 'Travel & Outdoor', icon: MapPin },
  { id: 'retail', name: 'Retail & Shopping', icon: Store },
]

const brands = [
  'Glen Ivy Hot Springs',
  'Glen\'s Army Navy',
  'Glenwood Springs',
  'Glen Ellen',
  'Glen Helen',
  'Glenfiddich',
  'Glen Canyon',
  'Glen Rose',
  'Gleneagles',
  'The Glenlivet',
  'Glen Ellyn',
  'Glendora',
  'Mapleglen',
]

export default function AllBrands() {
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [filteredCoupons, setFilteredCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedBrand, setSelectedBrand] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    document.title = 'All Glen Brands & Locations - Discover Deals | GlenCode'
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Browse all Glen-themed brands and locations. From Glen Ivy Hot Springs to Glenfiddich, find exclusive promo codes and deals across spa, dining, entertainment, and travel.'
      )
    }

    fetchCoupons()
  }, [])

  useEffect(() => {
    filterCoupons()
  }, [coupons, selectedCategory, selectedBrand, searchQuery])

  const fetchCoupons = async () => {
    try {
      setLoading(true)
      const result = await lumi.entities.coupons.list({
        filter: { active: true },
        sort: { clicks: -1 },
        limit: 100
      })
      setCoupons(result.list)
    } catch (error) {
      console.error('Failed to fetch coupons:', error)
    } finally {
      setLoading(false)
    }
  }

  const filterCoupons = () => {
    let filtered = [...coupons]

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(c => c.category === selectedCategory)
    }

    // Filter by brand
    if (selectedBrand !== 'all') {
      filtered = filtered.filter(c => c.brand === selectedBrand)
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(c =>
        c.title.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query) ||
        c.brand.toLowerCase().includes(query)
      )
    }

    setFilteredCoupons(filtered)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Store className="w-8 h-8 text-green-600" />
              <span className="text-green-700 font-semibold text-lg">Explore All Glen Brands</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Discover Every Glen Deal & Location
            </h1>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From spa retreats to whisky distilleries, entertainment venues to outdoor adventures – 
              find exclusive promo codes for all things "Glen"
            </p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="sticky top-0 bg-white border-b border-gray-200 shadow-sm z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search deals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Brand Filter */}
            <div className="relative">
              <Store className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">All Brands</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-900">
              {filteredCoupons.length} Deal{filteredCoupons.length !== 1 ? 's' : ''} Found
            </h2>
            
            {(selectedCategory !== 'all' || selectedBrand !== 'all' || searchQuery) && (
              <button
                onClick={() => {
                  setSelectedCategory('all')
                  setSelectedBrand('all')
                  setSearchQuery('')
                }}
                className="text-green-600 hover:text-green-700 font-medium"
              >
                Clear Filters
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-green-600 animate-spin" />
            </div>
          ) : filteredCoupons.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCoupons.map((coupon) => (
                <CouponCard
                  key={coupon._id}
                  title={coupon.title}
                  description={coupon.description}
                  discount={coupon.discount}
                  code={coupon.code}
                  expiryDate={coupon.expiryDate}
                  verified={coupon.verified}
                  affiliateLink={coupon.affiliateLink}
                  couponId={coupon._id}
                  onReveal={fetchCoupons}
                  showRatings={true}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Search className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No deals found</h3>
              <p className="text-gray-600 mb-6">
                Try adjusting your filters or search terms
              </p>
              <button
                onClick={() => {
                  setSelectedCategory('all')
                  setSelectedBrand('all')
                  setSearchQuery('')
                }}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Brand Showcase */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Featured Glen Brands & Locations
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {brands.map((brand) => (
              <button
                key={brand}
                onClick={() => {
                  setSelectedBrand(brand)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:shadow-lg transition text-center"
              >
                <Store className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">{brand}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Newsletter />
        </div>
      </section>
    </div>
  )
}
